import React, { useEffect, useState, useCallback } from "react";
import { Heart } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

/**
 * FavoriteButton — heart toggle for saving a Concierge.
 *
 * Behavior:
 *   - Unauthenticated → click navigates to /login (no API call).
 *   - Authenticated customer → POST /api/favorites or DELETE /api/favorites/{id}.
 *   - Demo profiles (is_demo_profile=true) → blocked at click; toast explains
 *     "Preview only — sign up to save".
 *   - Preview accounts → middleware returns 403; we surface a friendly toast
 *     instead of throwing.
 *
 * Visual state is purely local — does NOT optimistically modify a parent
 * list. Consumer can read `onChange(saved)` to refresh its own data.
 *
 * Renders as a small circular button overlay (white/glass) that sits in the
 * top-right of a concierge card or profile hero. Heart fills coral when saved.
 */
export default function FavoriteButton({
  conciergeUserId,
  isDemo = false,
  initialSaved = false,
  onChange,
  size = "md", // "sm" | "md" | "lg"
  testid,
}) {
  const { user } = useAuth();
  const [saved, setSaved] = useState(initialSaved);
  const [busy, setBusy] = useState(false);

  // If the consumer doesn't pass initialSaved, check the API once on mount.
  useEffect(() => { setSaved(initialSaved); }, [initialSaved]);

  const dims = size === "sm" ? "w-7 h-7" : size === "lg" ? "w-10 h-10" : "w-8 h-8";
  const iconSize = size === "sm" ? 12 : size === "lg" ? 18 : 14;

  const toggle = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    if (!user) {
      toast.info("Sign in to save Concierges.");
      window.location.href = "/login";
      return;
    }
    if (isDemo) {
      toast.info("Preview profile — sign up to save real Concierges.");
      return;
    }
    setBusy(true);
    try {
      if (saved) {
        await api.delete(`/favorites/${conciergeUserId}`);
        setSaved(false);
        onChange?.(false);
      } else {
        await api.post(`/favorites`, { concierge_user_id: conciergeUserId });
        setSaved(true);
        onChange?.(true);
        toast.success("Saved to your Concierges");
      }
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) {
        toast.info("Preview mode — sign up to save.");
      } else {
        toast.error(err?.response?.data?.detail || "Could not save right now.");
      }
    } finally {
      setBusy(false);
    }
  }, [busy, conciergeUserId, isDemo, onChange, saved, user]);

  return (
    <button
      type="button"
      data-testid={testid || `favorite-btn-${conciergeUserId}`}
      onClick={toggle}
      aria-label={saved ? "Remove from saved" : "Save concierge"}
      aria-pressed={saved}
      className={`${dims} inline-flex items-center justify-center rounded-full bg-[#FFF8EE]/95 backdrop-blur border border-[#071A2D]/10 shadow-sm hover:scale-105 active:scale-95 transition disabled:opacity-60`}
      disabled={busy}
    >
      <Heart
        size={iconSize}
        strokeWidth={2}
        className={`transition ${saved ? "text-[#FF7A45]" : "text-[#071A2D]/55"}`}
        fill={saved ? "#FF7A45" : "transparent"}
      />
    </button>
  );
}
